import { KeyIcon } from '@heroicons/react/24/outline';
import {
  Button,
  Chip,
  cn,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Pagination,
  Tooltip,
} from '@nextui-org/react';
import { Spinner } from '@nextui-org/spinner';
import lo from 'lodash';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ServerSchema, MonitorStatus, ServerUUID, ClusterTag } from '@common/types';
import { AddServer } from '@portal/components/AddServer';
import { InputSearchUI } from '@portal/components/common/InputSearch.ui';
import { NoteIconUI } from '@portal/components/common/NoteIcon.ui';
import { SelectClusterUI } from '@portal/components/common/SelectCluster.ui';
import { TableCountPageSizeSelectorUI } from '@portal/components/common/TableCountPageSizeSelector.ui';
import { TableNoDataUi } from '@portal/components/common/TableNoData.ui';
import { TerminalIconUI } from '@portal/components/common/TerminalIcon.ui';
import { TypographyUI } from '@portal/components/common/Typography.ui';
import { UnifiedNavigationUI } from '@portal/components/common/UnifiedNavigation.ui';
import { UserDropdown } from '@portal/components/UserDropdown';
import { connectionTypeMap } from '@portal/constant';
import { tsrestClient } from '@portal/provider/tsrest';
import { calcTotalPage, paginate, useAuthStatus } from '@portal/utils';
import { formatBites, formatBytes, getService, getTime, timesAgo } from '@portal/utils/stats';
import { Data, ServerStatsTableUI } from '@portal/views/monitors/components/ServerStats.Table.ui';

const createServiceStatus = (status?: MonitorStatus): { search: string; element: ReactNode } => {
  if (!status) {
    return {
      search: 'Unknown',
      element: (
        <Chip color='warning' size='sm' className='text-white'>
          Unknown
        </Chip>
      ),
    };
  }

  const lastAlive = timesAgo((status?.lastAlive && new Date(status?.lastAlive).getTime()) || 0);

  if (status.state === 'not-started') {
    return {
      search: 'Not Started',
      element: (
        <Chip color='warning' size='sm' className='text-white'>
          Not Started
        </Chip>
      ),
    };
  }

  if (status.state === 'initializing' || status.state === 'initializing-error') {
    return {
      search: 'Initializing',
      element: (
        <Tooltip
          content='Unknown server status, still initializing it'
          classNames={{
            content: 'bg-[#F9E79F]',
          }}
        >
          <Chip color='warning' size='sm' className='text-white'>
            Initializing
          </Chip>
        </Tooltip>
      ),
    };
  }

  if (
    status.state === 'standby' ||
    (status.state === 'checking' && !status.isAlive && !status.lastAlive) ||
    (status.state === 'checking-error' && !status.isAlive && !status.lastAlive)
  ) {
    return {
      search: 'Checking',
      element: (
        <Tooltip
          content='First time checking server'
          classNames={{
            content: 'bg-[#F9E79F]',
          }}
        >
          <Chip color='warning' size='sm' className='text-white'>
            Checking
          </Chip>
        </Tooltip>
      ),
    };
  }

  if (
    (status.state === 'checking' && !status.isAlive && status.lastAlive) ||
    (status.state === 'checking-error' && !status.isAlive && status.lastAlive)
  ) {
    return {
      search: 'Error',
      element: (
        <Tooltip
          content='Rechecking server, last check server is dead'
          classNames={{
            content: 'bg-[#F9E79F]',
          }}
        >
          <Chip color='danger' size='sm' className='text-white'>
            Error
          </Chip>
        </Tooltip>
      ),
    };
  }

  if ((status.state === 'checking' && status.isAlive) || (status.state === 'checking-error' && status.isAlive)) {
    return {
      search: 'Checking',
      element: (
        <Tooltip
          content={`Server is alive, last check is ${lastAlive}`}
          classNames={{
            content: 'bg-[#F9E79F]',
          }}
        >
          <Chip color='primary' size='sm' className='text-white'>
            Checking ({lastAlive})
          </Chip>
        </Tooltip>
      ),
    };
  }

  if (status.state === 'alive') {
    return {
      search: 'Alive',
      element: (
        <Tooltip
          content={`Server is alive, last check is ${lastAlive}`}
          classNames={{
            content: 'bg-[#F9E79F]',
          }}
        >
          <Chip color='success' size='sm' className='text-white'>
            Alive ({lastAlive})
          </Chip>
        </Tooltip>
      ),
    };
  }

  return {
    search: 'Failed',
    element: (
      <Tooltip
        content={status?.errorMessage}
        delay={0}
        classNames={{
          content: 'bg-[#F9E79F]',
        }}
      >
        <Chip color='danger' size='sm' className='text-white'>
          Failed
        </Chip>
      </Tooltip>
    ),
  };
};

const createConnectionType = (server: ServerSchema) => {
  let port = 0;
  if (server.connectionType === 'ssh' || server.connectionType === 'persistent-ssh') {
    port = server.sshPort;
  }
  if (server.connectionType === 'php') {
    port = server.phpPort;
  }

  return (
    <div>
      {connectionTypeMap[server.connectionType] || 'Unknown'}{' '}
      {port !== 0 && (
        <Chip size='sm' className='ml-1'>
          {port}
        </Chip>
      )}
    </div>
  );
};

const formatServerStats = (server: ServerSchema, status?: MonitorStatus, ramThreshold?: number) => {
  ramThreshold = ramThreshold || 1;

  const ss = createServiceStatus(status);
  const formatedStats: Data & {
    forSearch: string;
  } = {
    serverName: server.name,
    serverHost: server.host,
    connectionType: createConnectionType(server),
    clusterTag: server.clusterTag,
    ramUsage: '-',
    networkSpeed: '-',
    networkUsageToday: '-',
    networkUsageYesterday: '-',
    uptime: '-',
    service: '-',
    status: ss.element,
    schema: server,
    forSearch: ss.search,
  };

  const stats = status?.stats;
  if (!stats) return formatedStats;

  const usedRam = stats.memory[1] || 0;
  const totalRam = stats.memory[0] || 0;
  const usedRamPercent = ((stats.memory[1] || 1) / (stats.memory[0] || 1)) * 100;
  const ramString = `${usedRam.toFixed(2)}GB / ${totalRam.toFixed(2)}GB (${usedRamPercent.toFixed(2)}%)`;
  const ramUsage = (
    <Tooltip
      content={
        usedRam > ramThreshold ? `RAM usage exceeds threshold of ${ramThreshold.toFixed(2)}GB` : 'RAM usage is normal'
      }
      delay={0}
      classNames={{
        content: 'bg-[#F9E79F]',
      }}
    >
      <Chip color={usedRam > ramThreshold ? 'danger' : 'default'} size='sm'>
        {ramString}
      </Chip>
    </Tooltip>
  );

  const networkSpeedRaw = stats.network.txrx?.rx_kbps || 1;
  const networkSpeed = `${formatBites(networkSpeedRaw * 8000 || 0)}/s`;
  const networkSpeedChip = (
    <Chip
      color='default'
      size='sm'
      classNames={{
        base: cn(networkSpeedRaw < 125 && 'bg-[#08c1be]'),
      }}
    >
      {networkSpeed}
    </Chip>
  );

  const serviceRaw = getService(stats.services);
  const service = (
    <Tooltip
      content={status?.stats?.OS}
      delay={0}
      classNames={{
        content: 'bg-[#F9E79F]',
      }}
    >
      <Chip color={serviceRaw === 'No Service' ? 'danger' : 'default'} size='sm'>
        {serviceRaw}
      </Chip>
    </Tooltip>
  );

  formatedStats.ramUsage = ramUsage;
  formatedStats.forSearch += ` ${ramString}`;

  formatedStats.networkSpeed = networkSpeedChip;
  formatedStats.forSearch += ` ${networkSpeed}`;

  formatedStats.networkUsageToday = formatBytes(stats.network.used.today?.rx || 0);
  formatedStats.forSearch += ` ${formatedStats.networkUsageToday}`;

  formatedStats.networkUsageYesterday = formatBytes(stats.network.used.yesterday?.rx || 0);
  formatedStats.forSearch += ` ${formatedStats.networkUsageYesterday}`;

  formatedStats.uptime = getTime(stats.uptime || 0);
  formatedStats.forSearch += ` ${formatedStats.uptime}`;

  formatedStats.service = service;
  formatedStats.forSearch += ` ${serviceRaw}`;

  return formatedStats;
};

export const Monitors = () => {
  const { canAccessAdminFeatures } = useAuthStatus();
  const [searchParams, setSearchParams] = useSearchParams();

  const serversQuery = tsrestClient.servers.get.useQuery(
    ['servers'],
    {
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    },
    {
      refetchInterval: 1000,
    }
  );
  const statsQuery = tsrestClient.serversStats.get.useQuery(
    ['servers-stats'],
    {
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    },
    {
      refetchInterval: 1000,
    }
  );
  const ramThresholdQuery = tsrestClient.config.get.useQuery(['getRamThreshold'], {
    params: {
      key: 'notifier.ram.threshold',
    },
    headers: {
      authorization: localStorage.getItem('token') || '',
    },
  });

  const [serverSearch, setServerSearch] = useState('');
  const [selectedCluster, setSelectedCluster] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // SSL Password Reveal State
  const [showSslPasswords, setShowSslPasswords] = useState(false);
  const [sslPasswords, setSslPasswords] = useState<Record<ServerUUID, string | null>>({});
  const [sslPasswordLoading, setSslPasswordLoading] = useState<Record<ServerUUID, boolean>>({});
  const [sslPasswordErrors, setSslPasswordErrors] = useState<Record<ServerUUID, string>>({});

  const getSslPasswordsMutation = tsrestClient.servers.getSslPasswords.useMutation({
    onError: e => {
      console.error('Error fetching SSL passwords:', e);
    },
  });

  // Fetch SSL password for a single server
  const fetchSslPassword = async (serverUUID: ServerUUID, serverClusterTag: ClusterTag) => {
    // If cluster filter is active, only fetch for servers in the selected cluster
    if (selectedCluster !== 'all' && serverClusterTag !== selectedCluster) {
      return;
    }

    // Don't fetch if already loading or already fetched
    if (sslPasswordLoading[serverUUID] || sslPasswords[serverUUID] !== undefined) {
      return;
    }

    // Check if server has SSL enabled
    const isSslEnabled = statsQuery.data?.body.stats?.[serverUUID]?.stats?.services?.http_proxy?.active;
    if (!isSslEnabled) {
      // Mark as not SSL server
      setSslPasswords(prev => ({ ...prev, [serverUUID]: null }));
      return;
    }

    // Set loading state
    setSslPasswordLoading(prev => ({ ...prev, [serverUUID]: true }));

    try {
      const response = await getSslPasswordsMutation.mutateAsync({
        body: {
          serverUUIDs: [serverUUID],
        },
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      });

      if (response.status === 200 && response.body.passwords.length > 0) {
        const passwordData = response.body.passwords[0];
        setSslPasswords(prev => ({ ...prev, [serverUUID]: passwordData.password }));
        if (passwordData.error) {
          setSslPasswordErrors(prev => ({ ...prev, [serverUUID]: passwordData.error || 'Unknown error' }));
        }
      }
    } catch (error) {
      console.error(`Error fetching SSL password for server ${serverUUID}:`, error);
      setSslPasswordErrors(prev => ({ ...prev, [serverUUID]: 'Failed to retrieve password' }));
    } finally {
      setSslPasswordLoading(prev => {
        const newState = { ...prev };
        delete newState[serverUUID];
        return newState;
      });
    }
  };

  // SSL Password Reveal Handler
  const handleToggleSslPasswordReveal = () => {
    if (showSslPasswords) {
      // Hide passwords - clear all state
      setShowSslPasswords(false);
      setSslPasswords({});
      setSslPasswordLoading({});
      setSslPasswordErrors({});
    } else {
      // Show passwords column - passwords will be fetched one-by-one as rows are rendered
      setShowSslPasswords(true);
    }
  };

  const totalData = serversQuery.data?.body?.servers.length || 0;
  const filteredData = useMemo(() => {
    return lo(serversQuery.data?.body?.servers || [])
      .filter(s => (selectedCluster === 'all' ? true : s.clusterTag === selectedCluster))
      .map(s =>
        formatServerStats(
          s,
          statsQuery.data?.body.stats[s.uuid],
          parseFloat(ramThresholdQuery.data?.body?.value || '6.4')
        )
      )
      .filter(
        s =>
          s.serverName.toLowerCase().includes(serverSearch.toLowerCase()) ||
          s.serverHost.toLowerCase().includes(serverSearch.toLowerCase()) ||
          s.clusterTag.toLowerCase().includes(serverSearch.toLowerCase()) ||
          s.forSearch.toLowerCase().includes(serverSearch.toLowerCase())
      )
      .value();
  }, [serversQuery.data, serverSearch, selectedCluster]);
  const paginatedData = useMemo(() => paginate(filteredData, pageSize, page), [filteredData, pageSize, page]);
  const totalPage = useMemo(() => calcTotalPage(filteredData.length, pageSize), [filteredData.length, pageSize]);
  useEffect(() => {
    if (page > totalPage) setPage(totalPage);
  }, [page, totalPage]);

  useEffect(() => {
    if (!serversQuery.isFetched || !statsQuery.isFetched) return;
    setSearchParams({ cluster: selectedCluster, page: `${page}`, pageSize: `${pageSize}` });
    // Clear SSL passwords when cluster filter changes
    if (showSslPasswords) {
      setSslPasswords({});
      setSslPasswordLoading({});
      setSslPasswordErrors({});
    }
  }, [selectedCluster, page, pageSize, serversQuery.isFetched, statsQuery.isFetched]);

  useEffect(() => {
    if (!serversQuery.isFetched || !statsQuery.isFetched) return;
    setPage(parseInt(searchParams.get('page') || '1'));
    setPageSize(parseInt(searchParams.get('pageSize') || '100'));
    setSelectedCluster(searchParams.get('cluster') || 'all');
  }, [serversQuery.isFetched, statsQuery.isFetched]);

  const tableStatus = TableNoDataUi(
    serversQuery.isLoading,
    serversQuery.error !== null,
    (serversQuery.data?.body.servers.length || 0) === 0
  );

  return (
    <>
      {/* Navigation Header */}
      <div className='bg-white border-b border-gray-200 shadow-sm mb-4'>
        <div className='py-2'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-6'>
              <UnifiedNavigationUI />
              <div>
                <TypographyUI className='text-lg font-semibold' color='blue-gray'>
                  Monitors
                </TypographyUI>
                <TypographyUI className='text-sm' color='gray'>
                  Here is your server stats
                </TypographyUI>
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <TerminalIconUI />
              <NoteIconUI />
              <Tooltip
                content={
                  selectedCluster === 'all'
                    ? 'Perform actions on all servers'
                    : 'Perform actions on all servers in cluster'
                }
                classNames={{
                  content: 'bg-[#F9E79F]',
                }}
              >
                <Dropdown>
                  <DropdownTrigger>
                    <Button variant='bordered' color='primary' size='sm' startContent={<KeyIcon className='h-4 w-4' />}>
                      {selectedCluster === 'all' ? 'Server Actions' : 'Cluster Actions'}
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    onAction={key => {
                      if (key === 'reveal-ssl-password') {
                        handleToggleSslPasswordReveal();
                      }
                    }}
                    classNames={{
                      base: 'bg-transparent',
                      list: 'bg-transparent',
                    }}
                  >
                    <DropdownItem
                      key='reveal-ssl-password'
                      textValue={showSslPasswords ? 'Hide SSL Passwords' : 'Reveal SSL Passwords'}
                      className='bg-emerald-50 hover:bg-emerald-100 hover:shadow-md transition-all duration-200 text-gray-800 rounded-lg m-0 data-[hover=true]:bg-emerald-100 data-[hover=true]:shadow-md'
                    >
                      {showSslPasswords ? 'Hide SSL Passwords' : 'Reveal SSL Passwords'}
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </Tooltip>
              <SelectClusterUI
                value={selectedCluster}
                clusterOption={serversQuery.data?.body.cluster || []}
                onSelectedClusterChange={setSelectedCluster}
              />
              <InputSearchUI onSearchChange={setServerSearch} placeholder={'Search Server'} />
              {canAccessAdminFeatures && <AddServer />}
              <UserDropdown />
            </div>
          </div>
        </div>
      </div>

      <div className='flex w-full gap-4 flex-col'>
        <div className='flex flex-wrap justify-center gap-2'>
          <TableCountPageSizeSelectorUI
            totalData={totalData}
            totalFilteredData={filteredData.length}
            selectedPageSize={pageSize}
            setPageSize={setPageSize}
          />
          <ServerStatsTableUI
            paginatedData={paginatedData}
            tableStatus={tableStatus}
            showSslPasswords={showSslPasswords}
            sslPasswords={sslPasswords}
            sslPasswordLoading={sslPasswordLoading}
            sslPasswordErrors={sslPasswordErrors}
            onFetchSslPassword={fetchSslPassword}
            selectedCluster={selectedCluster}
            serverStats={statsQuery.data?.body.stats}
          />
          <Pagination isCompact showControls showShadow page={page} total={totalPage} onChange={setPage} />
        </div>
      </div>
    </>
  );
};
